<template>
  <div>
    <div class="page-breadcrumb">
      <span>文章管理</span> <i class="fas fa-chevron-right separator"></i> <span>{{ isEditMode ? '編輯文章' : '新增文章' }}</span>
    </div>

    <div v-if="isLoading && isEditMode" class="loading-spinner"> <p><i class="fas fa-spinner fa-spin"></i> 載入文章資料...</p>
    </div>

    <BilingualContentTabs ref="contentTabs" resource-type="article" :resource-id="activeArticleId" :source-revision="sourceRevision">
    <form v-show="!isLoading || !isEditMode" @submit.prevent="submitForm" class="edit-form-container">
      <div class="form-group">
        <label for="articleTitle">標題</label>
        <input type="text" id="articleTitle" data-testid="source-title" v-model="article.title" required class="form-input">
      </div>

      <div class="form-group">
        <label>封面圖片</label>
        <div class="image-upload-wrapper">
          <div class="image-preview">
            <img v-if="article.coverImageUrl" :src="article.coverImageUrl" alt="封面預覽">
            <div v-else class="image-placeholder-large"><i class="fas fa-image"></i></div>
          </div>
          <div class="upload-actions">
            <input type="file" data-testid="source-image" ref="fileInputRef" @change="handleCoverImageChange" accept="image/*" style="display: none;">
            <button type="button" class="modal-btn cancel-btn" @click="triggerFileInput" :disabled="isUploadingCover">
              <i v-if="!isUploadingCover" class="fas fa-upload"></i>
              <i v-else class="fas fa-spinner fa-spin"></i>
              {{ isUploadingCover ? '上傳中...' : '選擇封面' }}
            </button>
            <small>建議尺寸 800x450。僅上傳，不會立即儲存文章。</small>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label for="articleSummary">摘要 (預覽文字)</label>
        <textarea id="articleSummary" data-testid="source-summary" v-model="article.summary" rows="3" class="form-input"></textarea>
      </div>

      <div class="form-group">
        <label for="articleCategory">文章分類</label>
        <select id="articleCategory" v-model="article.categoryId" class="form-input">
          <option value="">請選擇分類</option>
          <option v-for="category in availableCategories" :key="category.id" :value="category.id">
            {{ category.name }}
          </option>
        </select>
      </div>

      <div class="form-group">
        <label for="articleTags">標籤</label>
        <div class="tags-input-wrapper">
          <div class="tag-input-container">
            <input 
              type="text" 
              id="articleTags" 
              v-model="newTagName" 
              @keydown.enter.prevent="addSingleTag"
              placeholder="輸入標籤名稱並按輸入鍵新增"
              class="form-input tags-input"
              :disabled="showColorPicker"
            />
            <button 
              type="button" 
              @click="addSingleTag" 
              class="add-tag-btn"
              :disabled="!newTagName.trim() || showColorPicker"
            >
              <i class="fas fa-plus"></i> 新增
            </button>
          </div>
          <small class="input-hint">一次只能新增一個標籤，輸入名稱後選擇顏色</small>
        </div>
        <div v-if="selectedTags.length > 0" class="selected-tags">
          <span v-for="tag in selectedTags" :key="tag.id || tag.name" class="tag-item" :style="{ backgroundColor: tag.color || '#3498db' }">
            {{ tag.name }}
            <button @click="removeTag(tag)" type="button" class="remove-tag-btn">×</button>
          </span>
        </div>
      </div>

      <!-- 顏色選擇器模態框 -->
      <div v-if="showColorPicker" class="color-picker-modal">
        <div class="color-picker-overlay" @click="cancelTagCreation"></div>
        <div class="color-picker-content">
          <h3>為標籤「{{ pendingTagName }}」選擇顏色</h3>
          <div class="color-options">
            <div 
              v-for="color in colorPalette" 
              :key="color"
              class="color-option"
              :class="{ active: selectedColor === color }"
              :style="{ backgroundColor: color }"
              @click="selectedColor = color"
            ></div>
          </div>
          <div class="custom-color-section">
            <label for="customColor">自訂顏色：</label>
            <input 
              type="color" 
              id="customColor" 
              v-model="selectedColor"
              class="custom-color-input"
            />
          </div>
          <div class="color-picker-actions">
            <button type="button" @click="cancelTagCreation" class="modal-btn cancel-btn">取消</button>
            <button type="button" @click="createTagWithColor" class="modal-btn save-btn">確認</button>
          </div>
        </div>
      </div>

      <div class="form-group editor-form-group">
        <label for="tinymce-editor">完整內容</label>
        <Editor
            id="source-content"
            v-model="article.content"
            :init="tinymceInitOptions"
            class="tinymce-editor-instance"
        />
        <small v-if="isEditorLoading" class="editor-loading-text"><i class="fas fa-spinner fa-spin"></i> 編輯器載入中...</small>
      </div>

      <div class="form-group toggle-form-group" style="justify-content: flex-start; gap: 20px;">
        <label for="articleEnabled" class="toggle-switch-label">發布狀態：</label>
        <label class="switch">
          <input type="checkbox" id="articleEnabled" v-model="article.enabled">
          <span class="slider round"></span>
        </label>
      </div>

      <div class="form-actions">
        <button type="button" @click="cancelForm" class="modal-btn cancel-btn">返回列表</button>
        <button type="submit" data-testid="source-save" class="modal-btn save-btn" :disabled="isSaving || isEditorLoading">
          <span v-if="isSaving"><i class="fas fa-spinner fa-spin"></i> 儲存中...</span>
          <span v-else>儲存文章</span>
        </button>
      </div>
    </form>
    </BilingualContentTabs>
    <button type="button" @click="cancelForm" class="modal-btn cancel-btn" style="margin-top:16px" data-testid="resource-back">返回文章列表</button>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch, nextTick } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import BilingualContentTabs from './BilingualContentTabs.vue';
import { adminError } from '@/utils/adminError';
import { guardBrowserClose } from '@/utils/unsavedChanges';
import Swal from '@/utils/chineseSwal';
import {createArticle, getArticleById, updateArticle} from '@/api/article.api.js';
import {uploadArticleImage, uploadArticleRichtextImage} from '@/api/file.api.js';
import { getEnabledArticleCategories } from '@/api/articleCategory.api.js';
import { getEnabledArticleTags, createArticleTag } from '@/api/articleTag.api.js';

// 導入 TinyMCE Vue component
import Editor from '@/components/LocalRichTextEditor.vue';

// --- Props & Emits ---
// eslint-disable-next-line no-undef
const props = defineProps({
  articleId: {
    type: Number,
    default: null
  }
});
// eslint-disable-next-line no-undef
const emit = defineEmits(['save-success', 'cancel']);

// --- State ---
const initialArticle = { title: '', content: '', enabled: false, coverImageUrl: '', summary: '', categoryId: '' };
const article = ref({ ...initialArticle });
const isLoading = ref(false); // 用於 fetchArticleData
const isSaving = ref(false);
const isUploadingCover = ref(false); // 專門給封面圖片上傳使用
const fileInputRef = ref(null);
const isEditorLoading = ref(true); // 用於追蹤 TinyMCE 是否載入完成

// 分類和標籤相關狀態
const availableCategories = ref([]);
const availableTags = ref([]);
const selectedTags = ref([]);
const newTagName = ref('');
const showColorPicker = ref(false);
const pendingTagName = ref('');
const selectedColor = ref('#3498db');
const colorPalette = ['#3498db', '#e74c3c', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c', '#34495e', '#e67e22', '#f1c40f', '#e91e63', '#8e44ad', '#2c3e50'];

const savedArticleId = ref(null);
const contentTabs = ref(null);
const sourceRevision = ref(0);
const sourceBaseline = ref('');
const activeArticleId = computed(() => props.articleId || savedArticleId.value);
const isEditMode = computed(() => !!activeArticleId.value);
const sourceState = () => JSON.stringify({ article: article.value, tags: selectedTags.value });

// --- TinyMCE Configuration ---

const tinymceInitOptions = ref({
  valid_elements: '*[*]',
  extended_valid_elements: 'script[src|type|async|defer],style[type],link[href|rel]',
  valid_children: '+body[style|script|link]',
  allow_script_urls: true,
  language: 'zh_TW', // 設定繁體中文
  // language_url: '/tinymce_langs/zh_TW.js', // 如果您自托管語言包，請取消註釋並設定正確路徑 (需要將語言包放入 public/tinymce_langs)
  plugins: 'preview importcss searchreplace autolink autosave save directionality code visualblocks visualchars fullscreen image link media table charmap pagebreak nonbreaking anchor insertdatetime advlist lists wordcount help charmap quickbars emoticons accordion',
  menubar: 'file edit view insert format tools table help',
  toolbar: 'undo redo | accordion accordionremove | blocks fontfamily fontsize | bold italic underline strikethrough | align numlist bullist | link image media table | lineheight outdent indent| forecolor backcolor removeformat | charmap emoticons | code fullscreen preview | save print | pagebreak anchor codesample | ltr rtl',
  toolbar_mode: 'sliding', // 'floating', 'sliding', 'wrap'
  autosave_ask_before_unload: true,
  autosave_interval: '30s',
  autosave_prefix: '{path}{query}-{id}-',
  autosave_restore_when_empty: false,
  autosave_retention: '2m',
  height: 600,
  skin: 'oxide-dark', // 使用暗黑主題皮膚
  content_css: 'dark', // 使用 TinyMCE 內建的暗黑內容 CSS
  font_family_formats: `
    Noto Sans TC='Noto Sans TC';
    微軟正黑體=Microsoft JhengHei;
    標楷體=DFKai-SB;
    新細明體=PMingLiU;
    Arial=arial,helvetica,sans-serif
  `,
  // 或者，提供一個自訂的 content_style 來更精確匹配您的頁面背景和文字顏色
  content_style: `
    body {
      background-color: #14141e; /* 編輯器內容區域背景色 (配合您的輸入框) */
      color: #e0e0e0; /* 編輯器內容文字顏色 */
      font-family: 'Noto Sans TC', 'Microsoft JhengHei', 'DFKai-SB', 'PMingLiU', sans-serif;
      line-height: 1.7;
    }
    a { color: #8be9fd; }
    img { max-width: 100%; height: auto; border-radius: 4px; }
    /* 您可以在這裡添加更多自訂樣式 */
  `,
  branding: false, // 移除 "Powered by TinyMCE"
  promotion: false, // 移除 TinyMCE 廣告 (如果您的帳戶類型允許)
  image_advtab: true,
  image_caption: true,
  quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote quickimage quicktable',
  noneditable_class: 'mceNonEditable',
  contextmenu: 'link image table',
  // 圖片上傳處理 (重要)
  // eslint-disable-next-line no-async-promise-executor
  images_upload_handler: async (blobInfo) => new Promise(async (resolve, reject) => {
    // 可以為編輯器內的圖片上傳使用單獨的 isUploading 狀態
    // let editorImageUploading = ref(true); // 如果需要單獨的 loading 提示
    try {
      const file = blobInfo.blob();
      const response = await uploadArticleRichtextImage(file);
      if (response && response.url) {
        resolve(response.url); // TinyMCE 期望返回圖片的 URL
      } else {
        reject({ message: '圖片上傳成功，但未收到有效的 URL。', remove: true });
      }
    } catch (error) {
      const errorMessage = adminError(error, '圖片上傳失敗，請檢查檔案後重試。');
      reject({ message: errorMessage, remove: true });
    } finally {
      // editorImageUploading.value = false;
    }
  }),
  file_picker_types: 'image media', // 允許圖片和媒體文件選擇器
  // file_picker_callback: (cb, value, meta) => { ... } // 如果需要自訂文件選擇器
  init_instance_callback: (editor) => {
    console.log('TinyMCE initialized:', editor);
    isEditorLoading.value = false; // 編輯器載入完成
    // 如果是編輯模式且有內容，設定編輯器內容
    if (isEditMode.value && article.value.content) {
      editor.setContent(article.value.content);
    } else if (!isEditMode.value) {
      editor.setContent(''); // 新增模式確保內容為空
    }
  }
});


// --- Functions ---
const fetchArticleData = async () => {
  if (!isEditMode.value) {
    article.value = { ...initialArticle, content: '' }; // 新增時，確保 content 是空字串
    isEditorLoading.value = false; // 新增模式下，編輯器可以立即使用 (假設已配置API Key)
    await nextTick(); // 給 editor 一點時間渲染
    // 如果 TinyMCE 實例已存在 (例如通過 v-if 重新顯示)，可能需要手動設定空內容
    // 但通常 v-model 會處理好，或者 init_instance_callback 會處理
    return;
  }
  isLoading.value = true;
  isEditorLoading.value = true; // 開始載入數據，編輯器也視為載入中
  try {
    const response = await getArticleById(activeArticleId.value);
    article.value = { ...initialArticle, ...response };
    if (article.value.content === null || article.value.content === undefined) {
      article.value.content = ''; // 確保 content 不是 null/undefined
    }
    
    // 設置分類ID
    if (response.category) {
      article.value.categoryId = response.category.id;
    }
    
    // 設置標籤
    selectedTags.value = [...(response.tags || [])];
    sourceBaseline.value = sourceState();
    
    // content 將通過 v-model 或 init_instance_callback 傳遞給 TinyMCE
  } catch (error) {
    Swal.fire({ title: '錯誤', text: '無法載入文章資料', icon: 'error', customClass: { popup: 'swal-dark-theme' } });
    emit('cancel');
    isEditorLoading.value = false; // 出錯時，也標記編輯器載入結束 (或失敗)
  } finally {
    isLoading.value = false;
    isEditorLoading.value = false;
    // isEditorLoading 會在 init_instance_callback 中設為 false
  }
};

const triggerFileInput = () => {
  fileInputRef.value.click();
};

const handleCoverImageChange = async (event) => { // 更名以區分
  const file = event.target.files[0];
  if (!file) return;

  isUploadingCover.value = true;
  try {
    const response = await uploadArticleImage(file);
    article.value.coverImageUrl = response.url;
    Swal.fire({ icon: 'success', title: '封面圖片上傳成功', showConfirmButton: false, timer: 1500, customClass: { popup: 'swal-dark-theme' } });
  } catch (error) {
    Swal.fire({ title: '封面圖片上傳失敗', text: adminError(error, '請檢查檔案或網路後重試。'), icon: 'error', customClass: { popup: 'swal-dark-theme' } });
  } finally {
    isUploadingCover.value = false;
    event.target.value = '';
  }
};

// 載入分類和標籤
const fetchCategories = async () => {
  try {
    const categories = await getEnabledArticleCategories();
    availableCategories.value = categories;
  } catch (error) {
    console.error('載入分類失敗:', error);
  }
};

const fetchTags = async () => {
  try {
    const tags = await getEnabledArticleTags();
    availableTags.value = tags;
  } catch (error) {
    console.error('載入標籤失敗:', error);
  }
};

// 新增單個標籤
const addSingleTag = async () => {
  const tagName = newTagName.value.trim();
  if (!tagName) return;
  
  // 檢查是否已存在
  let existingTag = availableTags.value.find(tag => tag.name === tagName);
  
  if (existingTag) {
    // 標籤已存在，直接添加到選中標籤中（避免重複）
    if (!selectedTags.value.find(tag => tag.id === existingTag.id)) {
      selectedTags.value.push(existingTag);
    }
    newTagName.value = '';
    return;
  }
  
  // 標籤不存在，顯示顏色選擇器
  pendingTagName.value = tagName;
  selectedColor.value = '#3498db'; // 重置為預設顏色
  showColorPicker.value = true;
};

// 創建標籤並選擇顏色
const createTagWithColor = async () => {
  try {
    const newTag = await createArticleTag({
      name: pendingTagName.value,
      description: `標籤: ${pendingTagName.value}`,
      color: selectedColor.value,
      enabled: true
    });
    availableTags.value.push(newTag);
    
    // 添加到選中標籤中
    if (!selectedTags.value.find(tag => tag.id === newTag.id)) {
      selectedTags.value.push(newTag);
    }
    
    // 關閉顏色選擇器並清空輸入
    showColorPicker.value = false;
    pendingTagName.value = '';
    newTagName.value = '';
  } catch (error) {
    console.error('創建標籤失敗:', error);
    Swal.fire({ title: '新增標籤失敗', text: adminError(error), icon: 'error' });
    showColorPicker.value = false;
    pendingTagName.value = '';
  }
};

// 取消創建標籤
const cancelTagCreation = () => {
  showColorPicker.value = false;
  pendingTagName.value = '';
};

// 移除標籤
const removeTag = (tagToRemove) => {
  selectedTags.value = selectedTags.value.filter(tag => tag.id !== tagToRemove.id);
};


const submitForm = async () => {
  if (isEditorLoading.value) {
    Swal.fire({ title: '提示', text: '編輯器仍在載入中，請稍候。', icon: 'info', customClass: { popup: 'swal-dark-theme' } });
    return;
  }
  isSaving.value = true;
  // article.content 已通過 v-model 與 TinyMCE 綁定
  try {
    // 準備要提交的文章數據
    const articleData = {
      ...article.value,
      category: article.value.categoryId ? { id: article.value.categoryId } : null,
      tags: selectedTags.value.map(tag => ({ id: tag.id, name: tag.name, color: tag.color }))
    };
    
    if (isEditMode.value) {
      await updateArticle(activeArticleId.value, articleData);
    } else {
      const created = await createArticle(articleData);
      savedArticleId.value = created.id;
    }
    Swal.fire({ title: '儲存成功！', icon: 'success', timer: 1500, showConfirmButton: false, customClass: { popup: 'swal-dark-theme' }});
    sourceBaseline.value = sourceState();
    sourceRevision.value += 1;
  } catch (error) {
    Swal.fire({ title: '儲存失敗', text: adminError(error), icon: 'error', customClass: { popup: 'swal-dark-theme' } });
  } finally {
    isSaving.value = false;
  }
};

// --- Hooks ---
onMounted(async () => { await Promise.all([fetchCategories(), fetchTags()]); });

// 監聽 articleId 的變化，以便在同一個子組件內切換編輯不同文章時重新載入數據
watch(() => props.articleId, (newId, oldId) => {
  if (newId !== oldId) { // 確保 ID 真的改變了
    savedArticleId.value = null;
    if (newId !== null) { // 編輯模式
      fetchArticleData();
    } else { // 從編輯切換到新增模式
      article.value = { ...initialArticle, content: '' };
      selectedTags.value = [];
      sourceBaseline.value = sourceState();
      isEditorLoading.value = false;
      // TinyMCE 實例可能需要手動重設內容，或依賴 init_instance_callback
    }
  }
}, { immediate: true }); // immediate: true 確保組件初次創建時，如果 articleId 已有值，也會執行

const confirmSourceDiscard = () => !sourceBaseline.value || sourceBaseline.value === sourceState() || window.confirm('中文文章尚未儲存，確定要捨棄修改嗎？');
const cancelForm = () => { if (confirmSourceDiscard() && (!contentTabs.value || contentTabs.value.confirmDiscard())) emit('cancel'); };
onBeforeRouteLeave(() => confirmSourceDiscard());
guardBrowserClose(() => sourceBaseline.value && sourceBaseline.value !== sourceState());
</script>

<style scoped>
/* 沿用之前的 form-group, modal-btn 等樣式 */
.edit-form-container {
  background-color: rgba(30, 30, 40, 0.7);
  padding: 30px 40px;
  border-radius: 8px;
  border: 1px solid rgba(160, 100, 220, 0.3);
}

.image-upload-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.image-preview, .image-placeholder-large {
  width: 240px;
  height: 135px;
  border-radius: 4px;
  background-color: rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  flex-shrink: 0;
}
.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}
.image-placeholder-large {
  font-size: 3em;
}

.upload-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.upload-actions small {
  color: #888e95;
  font-size: 0.85em;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(160, 100, 220, 0.15);
}

/* 沿用上一版 modal 的樣式 */
.modal-btn { padding: 10px 20px; border: none; border-radius: 5px; font-weight: 500; cursor: pointer; }
.modal-btn.cancel-btn { background-color: rgba(80, 80, 90, 0.7); color: #c0c0c0; }
.modal-btn.cancel-btn:hover { background-color: rgba(90, 90, 100, 0.8); }
.modal-btn.save-btn { background: linear-gradient(145deg, #50fa7b, #3ddc67); color: #1a1a1a; }
.modal-btn.save-btn:hover:not(:disabled) { background: linear-gradient(145deg, #60fc8b, #4dfd77); }
.modal-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.modal-btn i { margin-right: 5px; }
.form-input { width: 100%; padding: 10px 12px; background-color: rgba(20, 20, 30, 0.8); border: 1px solid rgba(160, 100, 220, 0.4); border-radius: 5px; color: #e0e0e0; }
.form-group { margin-bottom: 20px; }
.form-group label { display: block; margin-bottom: 8px; color: #a0a0b0; }
textarea.form-input { height: auto; }
.switch { position: relative; display: inline-block; width: 50px; height: 28px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(80, 80, 90, 0.7); transition: .3s; border: 1px solid rgba(120, 120, 130, 0.5); }
.slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 3px; bottom: 3px; background-color: #e0e0e0; transition: .3s; }
input:checked + .slider { background-color: #50fa7b; border-color: #3ddc67; }
input:checked + .slider:before { transform: translateX(22px); background-color: #1a1a1a; }
.slider.round { border-radius: 28px; }
.slider.round:before { border-radius: 50%; }
.toggle-switch-label { margin: 0; }

/* TinyMCE Editor Wrapper Styles */
.editor-form-group {
  position: relative; /* For loading text positioning */
}
.tinymce-editor-instance {
  /* Vue 組件本身可能不需要太多樣式，主要靠 init 配置中的 skin 和 content_css/content_style */
  border-radius: 5px; /* 可以嘗試給 wrapper 一個邊框 */
  overflow: hidden; /* 確保 skin 的圓角生效 */
  border: 1px solid rgba(160, 100, 220, 0.4); /* 與 form-input 邊框色一致 */
}

.editor-loading-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #a0a0b0;
  font-size: 0.9em;
  z-index: 10; /* 確保在編輯器之上，直到編輯器載入完成 */
}


/* 如果需要更細緻地調整 TinyMCE UI，可以使用 :deep() */
/* 例如，確保工具欄背景和您的主題更搭 */
:deep(.tox .tox-toolbar__primary, .tox .tox-toolbar__secondary) {
  background-color: rgba(35, 35, 50, 0.8) !important; /* 更深的工具欄背景 */
}
:deep(.tox .tox-edit-area__iframe) {
  background-color: #14141e !important; /* iframe 背景，content_style 會覆蓋這裡的 body 背景 */
}
:deep(.tox .tox-statusbar) {
  background-color: rgba(35, 35, 50, 0.8) !important;
  color: #a0a0b0 !important;
  border-top: 1px solid rgba(160, 100, 220, 0.4) !important;
}
:deep(.tox .tox-statusbar a) {
  color: #8be9fd !important;
}
:deep(.tox .tox-tbtn svg) {
  fill: #a0a0b0 !important; /* 按鈕圖示顏色 */
}
:deep(.tox .tox-tbtn:hover svg) {
  fill: #e0e0e0 !important;
}
:deep(.tox .tox-mbtn, .tox .tox-tbtn) {
  color: #a0a0b0 !important; /* 按鈕文字顏色 */
}
:deep(.tox .tox-mbtn:hover, .tox .tox-tbtn--enabled, .tox .tox-tbtn:focus) {
  background-color: rgba(60, 60, 75, 0.9) !important;
  color: #e0e0e0 !important;
}
:deep(.tox .tox-tbtn--select) { /* 例如字體大小選擇 */
  color: #a0a0b0 !important;
}
:deep(.tox .tox-tbtn--select .tox-tbtn__select-label) {
  color: #a0a0b0 !important;
}

/* 標籤輸入樣式 */
.tags-input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.tag-input-container {
  display: flex;
  gap: 10px;
  align-items: center;
}

.tags-input {
  flex: 1;
}

.add-tag-btn {
  padding: 10px 16px;
  background: linear-gradient(145deg, #50fa7b, #3ddc67);
  color: #1a1a1a;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.9em;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
}

.add-tag-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #60fc8b, #4dfd77);
  box-shadow: 0 2px 8px rgba(80, 250, 123, 0.3);
}

.add-tag-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: rgba(80, 80, 90, 0.5);
  color: #a0a0b0;
}

.input-hint {
  color: #888e95;
  font-size: 0.85em;
  font-style: italic;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
  padding: 10px;
  background-color: rgba(20, 20, 30, 0.7);
  border: 1px solid rgba(160, 100, 220, 0.2);
  border-radius: 4px;
  min-height: 40px;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  color: white;
  font-size: 0.85em;
  font-weight: 500;
  gap: 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.remove-tag-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 1.1em;
  font-weight: bold;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
}

.remove-tag-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

/* 下拉選單樣式 */
select.form-input {
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23a0a0b0' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 8px center;
  background-repeat: no-repeat;
  background-size: 16px;
  padding-right: 40px;
  appearance: none;
}

/* 顏色選擇器模態框樣式 */
.color-picker-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.color-picker-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
}

.color-picker-content {
  position: relative;
  background-color: rgba(30, 30, 40, 0.95);
  border: 1px solid rgba(160, 100, 220, 0.5);
  border-radius: 12px;
  padding: 30px;
  min-width: 400px;
  max-width: 500px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

.color-picker-content h3 {
  margin: 0 0 20px 0;
  color: #e0e0e0;
  font-size: 1.2em;
  text-align: center;
}

.color-options {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 25px;
}

.color-option {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.2s ease;
  position: relative;
}

.color-option:hover {
  transform: scale(1.1);
  border-color: rgba(255, 255, 255, 0.3);
}

.color-option.active {
  border-color: #50fa7b;
  transform: scale(1.15);
  box-shadow: 0 0 15px rgba(80, 250, 123, 0.5);
}

.color-option.active::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-weight: bold;
  font-size: 16px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
}

.custom-color-section {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
  padding: 15px;
  background-color: rgba(20, 20, 30, 0.7);
  border-radius: 8px;
  border: 1px solid rgba(160, 100, 220, 0.2);
}

.custom-color-section label {
  color: #a0a0b0;
  font-weight: 500;
  margin: 0;
}

.custom-color-input {
  width: 60px;
  height: 40px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: none;
  outline: none;
}

.color-picker-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
}
</style>
